import { FormControl } from '@angular/forms';

import { CatsRecordTypeEnum } from '@shared-enums/cats.enum';
import { IForemanCatsProperties, ISkfConfigEntity, ISupportedSkfTypes } from '@shared-interfaces/api';

import { ICreateProductionText, IModifyCatsRecordText } from '../models/cats-outbound.model';
import { DatahubService } from '../services/datahub.service';

export class ProductionText {
  value = new FormControl<string | boolean>('', { nonNullable: true });
  description: string;
  catsRecord?: IForemanCatsProperties;
  skfNumber: string;
  prdGrpID = '';
  type: ISupportedSkfTypes;
  selectOptions: ISelectOptions[] = [];

  private hasChanged = false;
  private isNew = true;

  constructor(
    skfConfig: ISkfConfigEntity,
    public datahub: DatahubService,
    groupNumber?: string,
  ) {
    this.description = skfConfig.CustDesc;
    this.skfNumber = skfConfig.SKF;
    this.type = skfConfig.Type;
    if ((skfConfig.SkfToValue.results?.length ?? 0) > 0) this.selectOptions = this.makeSelectOptions(skfConfig);
    if (groupNumber) this.prdGrpID = groupNumber;
    this.value.valueChanges.subscribe(() => {
      //when the value changes in the form, update the catsrecord attached to this production entity, and flag this record for upload
      this.hasChanged = !this.value.pristine;
    });
  }

  getCatRecords(): ICreateProductionText | IModifyCatsRecordText | null {
    //TODO: This method is crazy. It can return null, CatsRecord, or a different object we don't have a type for! This needs to be narrowed down to make sense!
    if (!this.hasChanged) return null;
    if (this.isNew && !this.value.value) return null; //we don't want to submit blank values.
    if (this.catsRecord) {
      if (!this.value.value) {
        this.datahub.timesheet.flagForDelete(this);
        return null;
      }
      return this.formatModify(this.catsRecord);
    }
    return this.makeProductionSkfCatRecord();
  }

  makeSelectOptions(skfConfig: ISkfConfigEntity): ISelectOptions[] {
    const blank: ISelectOptions = {
      value: '',
      display: '',
    };
    const options = (skfConfig.SkfToValue.results ?? []).map((x) => this.createSelectOption(x.Fldval, x.Fldval));
    options.push(blank);
    return options;
  }

  makeProductionSkfCatRecord(): ICreateProductionText {
    return {
      ZempCreatedBy: this.datahub.timesheet.findTokenEmployee(),
      Extdocumentno: this.datahub.extDocument.Documentno,
      Zmatnr: this.datahub.contract.getProductionDefault(this.skfNumber),
      Employeenumber: this.datahub.timesheet.findTokenEmployee(),
      Quantity: '1',
      Longtext: true,
      LongTextstr: this.value.value.toString(),
      Workdate: this.datahub.date.getOdataString(),
      RecOrder: this.datahub.contract.getServiceOrder(),
      Zrecordtype: CatsRecordTypeEnum.ProductionText,
      Statkeyfig: this.skfNumber,
      ZprdGrpID: this.prdGrpID,
    };
  }

  setState(catsRecord: IForemanCatsProperties): void {
    this.value.setValue(catsRecord.LongTextstr);
    this.catsRecord = catsRecord;
    this.isNew = false;
    this.hasChanged = false;
  }

  getGraphableValues() {
    return [this.description, this.value.value];
  }

  findCounters(): string {
    if (this.catsRecord?.Counter) return this.catsRecord.Counter;
    else return '';
  }

  private formatModify(record: IForemanCatsProperties): IModifyCatsRecordText {
    //the only thing we should be able to change in the foreman screen is the quantity.
    return {
      LongTextstr: this.value.value.toString(),
      LongTextstr_X: true,
      Counter: record.Counter,
    };
  }

  private createSelectOption(value: string, display: string): ISelectOptions {
    return { value, display };
  }
}

export interface ISelectOptions {
  value: string;
  display: string;
}
